import customEventBus from '@common/scripts/libs/eventBus';
import useChartTable from '@common/utils/useChartTable';
import SizeSelectorModalItem from '@moncler/components/product/SizeSelectorModalItem';
import OutOfStockBlock from '@moncler/components/product/OutOfStockBlock';
import ModalDialog from '@ui/modal/ModalDialog';
import useBreakpoint from '@utils/useBreakpoint';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import FocusTrap from 'focus-trap-react';
const SizeSelectorButton = (props) => {
    var _a;
    const { t } = useTranslation();
    const [openModal, setOpenModal] = useState();
    const currentProduct = props.current;
    const isComingSoon = (currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.isComingSoon) == 2;
    const showNotifyMe = useMemo(() => {
        var _a, _b, _c, _d;
        return (window.unshoppable ||
            (currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.isBoutiqueExclusive) ||
            ((_a = currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.availability) === null || _a === void 0 ? void 0 : _a.isOutOfStock) ||
            isComingSoon ||
            (currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.notShoppable) ||
            !((_b = currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.price) === null || _b === void 0 ? void 0 : _b.type) ||
            !((_d = (_c = currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.price) === null || _c === void 0 ? void 0 : _c.sales) === null || _d === void 0 ? void 0 : _d.value));
    }, [currentProduct]);
    const [countrySizes, setCountrySizes] = useState();
    const [monclerSizes, setMonclerSizes] = useState();
    useChartTable(props.sizeChart, setCountrySizes, setMonclerSizes);
    const currentBreakpoint = useBreakpoint();
    const [bottom, setBottom] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const scrollbarStartingHeight = 53;
    const [scrollBarHeight, setScrollBarHeight] = useState(scrollbarStartingHeight);
    const [longestSizeChars, setLongestSizeChars] = useState(0);
    const [isMobile, setIsMobile] = useState();
    const [focusedOption, setFocusedOption] = useState();
    const [notifyOpen, setNotifyOpen] = useState();
    const scrollableBox = useRef(null);
    useEffect(() => {
        const onSizeOpen = () => {
            setOpenModal(true);
        };
        customEventBus.on('size-selector:open', onSizeOpen);
        return () => {
            customEventBus.off('size-selector:open', onSizeOpen);
        };
    }, []);
    useEffect(() => {
        const isMobile = currentBreakpoint < parseInt(window.mqObj.md.toString());
        setIsMobile(isMobile);
        if (!isMobile) {
            setModalOffset();
        }
    }, [currentBreakpoint]);
    const showSimilarProductsOOS = useMemo(() => {
        var _a, _b, _c, _d, _e;
        const hasLimitedAvailability = (currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.hasLimitedAvailability) || (currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.isComingSoon);
        const isManualConfig = ((_c = (_b = (_a = currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.limitedAvailabilityConfig) === null || _a === void 0 ? void 0 : _a.similarProductConfig) === null || _b === void 0 ? void 0 : _b.productIdList) === null || _c === void 0 ? void 0 : _c.length) > 0;
        //In case of manual config, the ajax call for tiles is postponed when rendering is needed
        const hasProductTiles = ((_d = props.similarProductsTiles) === null || _d === void 0 ? void 0 : _d.length) > 0 || isManualConfig;
        const showSimilarProducts = (_e = currentProduct === null || currentProduct === void 0 ? void 0 : currentProduct.limitedAvailabilityConfig) === null || _e === void 0 ? void 0 : _e.showSimilarProducts;
        return hasLimitedAvailability && showSimilarProducts && hasProductTiles;
    }, [currentProduct, props.similarProductsTiles]);
    const fittingCaption = useCallback(() => {
        var _a, _b;
        return ((((_a = props.current) === null || _a === void 0 ? void 0 : _a.fitting) || ((_b = props.current) === null || _b === void 0 ? void 0 : _b.sizeAndFit1)) && (React.createElement("div", { className: "fitting-caption" },
            React.createElement("div", { className: "fitting" },
                React.createElement("strong", null,
                    t('label.fitting'),
                    ": "),
                React.createElement("span", null, props.current.fitting || props.current.sizeAndFit1)))));
    }, [props.current]);
    const sizeLabel = (size) => {
        var _a;
        const monclerSizeIndex = monclerSizes === null || monclerSizes === void 0 ? void 0 : monclerSizes.rawValues.indexOf(size);
        const countrySize = countrySizes === null || countrySizes === void 0 ? void 0 : countrySizes.values[monclerSizeIndex];
        const convertedSize = countrySize && `${countrySizes.id} ${countrySize}`;
        const isSuggestedSize = ((_a = props.suggestedSize) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === size.toLowerCase();
        if (!convertedSize && !isSuggestedSize) {
            return null;
        }
        return (React.createElement(React.Fragment, null,
            convertedSize && React.createElement("span", { className: "size-selector-line-label" }, convertedSize),
            isSuggestedSize && React.createElement("span", { className: "size-selector-line-label" },
                " ",
                t('sizeguide.reccommendedsize'))));
    };
    const isSizeDisabled = (variation) => {
        const variationsLength = props.current.isParfum ? props.current.parfumAttributes.productFamily : props.variationValues;
        return props.alwaysAvailable || variationsLength.length < 2 ? false : isComingSoon || (!variation.selectable && !props.boutiqueAvailability && !window.unshoppable);
    };
    const limitedAvailabilityDepth = useMemo(() => {
        var _a, _b;
        const isLimitedAvailabilityDepth = currentProduct.hasLimitedAvailabilityDepth && ((_a = props.currentSize) === null || _a === void 0 ? void 0 : _a.hasSizeLimitedAvailability);
        if (isLimitedAvailabilityDepth && ((_b = props.currentSize) === null || _b === void 0 ? void 0 : _b.selectable) && !currentProduct.isBoutiqueExclusive) {
            return React.createElement("span", { className: "size-selector-line-label size-limited-availability" }, t('product.limitedavailability'));
        }
    }, [currentProduct, props.currentSize]);
    const setModalOffset = () => {
        const selectorContainers = document.querySelector('.selector-size-expanded');
        const buttonContainers = document.querySelector('.button-modal-container');
        if (selectorContainers && buttonContainers) {
            const buttonContainer = buttonContainers;
            const selectorContainer = selectorContainers;
            // Ottiene altezze e margini degli elementi e calcola l'altezza totale
            const marginTop = parseInt(window.getComputedStyle(buttonContainer).marginTop);
            const paddingBottom = parseInt(window.getComputedStyle(selectorContainer).paddingBottom);
            const height = buttonContainer.offsetHeight + marginTop + paddingBottom - 1;
            // Imposta l'altezza calcolata nello state
            setBottom(height);
        }
    };
    const buttonText = useMemo(() => {
        if (props.currentSize) {
            return (React.createElement("span", { className: "size-selection-button-label" },
                React.createElement("span", { className: "bold" }, props.currentSize.displayValue),
                sizeLabel(props.currentId),
                limitedAvailabilityDepth));
        }
        else {
            return t('label.selectasize');
        }
    }, [currentProduct, props.currentId, props.suggestedSize, props.currentSize]);
    useEffect(() => {
        if (openModal) {
            setFocusedOption(0);
        }
        else {
            setFocusedOption(undefined);
        }
    }, [openModal]);
    const focusOption = (dir) => {
        var _a;
        if (dir == 'next') {
            setFocusedOption(focusedOption + 1 < ((_a = props.variationValues) === null || _a === void 0 ? void 0 : _a.length) ? focusedOption + 1 : focusedOption);
        }
        else {
            setFocusedOption(focusedOption - 1 <= 0 ? 0 : focusedOption - 1);
        }
    };
    const calculateScrollTop = (scrollValue) => {
        if (!scrollableBox.current) {
            return;
        }
        const scrollableElement = scrollableBox.current;
        const scrollableLength = scrollableElement.scrollHeight - scrollableElement.clientHeight;
        const scrollTrackHeight = scrollableElement.scrollHeight - scrollBarHeight;
        const scrollTop = (scrollValue / scrollableLength) * scrollTrackHeight;
        const scrollingDelta = scrollTop < 0 ? scrollTop : scrollTop > scrollTrackHeight ? scrollTop - scrollTrackHeight : 0;
        const bouncingBar = scrollbarStartingHeight - Math.abs(scrollingDelta) / 5;
        setScrollTop(Math.min(Math.max(0, scrollTop), scrollTrackHeight));
        if (bouncingBar !== scrollbarStartingHeight) {
            setScrollBarHeight(bouncingBar);
        }
    };
    const parfumLinkToSelectorItem = (parfumLink) => {
        return Object.assign(Object.assign({}, parfumLink), { isParfum: true, displayValue: parfumLink.format, value: parfumLink.format, selectable: parfumLink.available, selected: false, preorderable: false, refinementColor: null, swatchColor: null, swatchImg: null, imgs: null });
    };
    const itemList = useMemo(() => {
        return (props.current.isParfum ? props.current.parfumAttributes.productFamily.map((pf) => parfumLinkToSelectorItem(pf)) : props.variationValues);
    }, [props.current]);
    const SizesBlock = useMemo(() => {
        return (React.createElement("ul", { id: "size-selector-container", className: (itemList === null || itemList === void 0 ? void 0 : itemList.length) <= 8 ? 'hide__scrollbar' : '', role: "listbox", "aria-labelledby": "size-combobox", tabIndex: -1, ref: scrollableBox, onScroll: (e) => {
                calculateScrollTop(e.target.scrollTop);
            } }, itemList === null || itemList === void 0 ? void 0 : itemList.map((item, i) => {
            var _a;
            if (((_a = item.displayValue) === null || _a === void 0 ? void 0 : _a.length) > longestSizeChars) {
                setLongestSizeChars(item.displayValue.length);
            }
            return (React.createElement(SizeSelectorModalItem, { key: item.id, index: i, selectorItem: item, selected: item.value == props.currentId, disabled: isSizeDisabled(item), variationGroupId: props.current.id, isSizeInStickyTray: props.isSizeInStickyTray, isQuickBuy: props.isQuickBuy, onSelect: () => {
                    toggleModal(false);
                    props.onChangeVariation(item);
                }, toggleModal: () => toggleModal(false), extraLabel: sizeLabel(item.value), showNotifyMe: showNotifyMe, product: currentProduct, focusOption: focusOption, focusedOption: focusedOption, onNotifyOpen: (open) => setNotifyOpen(open) }));
        })));
    }, [props.currentId, props.variationValues, countrySizes, monclerSizes, props.suggestedSize, focusedOption]);
    const closeModalOnClickOutside = (e) => {
        const HTMLtarget = e.target;
        if (!HTMLtarget.closest('.size-selection-combobox') && !HTMLtarget.closest('#notifyme') && !HTMLtarget.closest('.size-selection-button')) {
            setOpenModal(false);
            document.body.removeEventListener('mousedown', closeModalOnClickOutside);
        }
        else if (HTMLtarget.closest('.size-selection-button')) {
            document.body.removeEventListener('mousedown', closeModalOnClickOutside);
        }
    };
    const toggleModal = (open) => {
        setOpenModal(open);
        if (!isMobile) {
            if (open) {
                document.body.addEventListener('mousedown', closeModalOnClickOutside);
            }
            else {
                document.body.removeEventListener('mousedown', closeModalOnClickOutside);
            }
        }
    };
    return ((_a = props.productAvailability) === null || _a === void 0 ? void 0 : _a.isOutOfStock) && !props.current.isBoutiqueExclusive && !props.current.isParfum ? (React.createElement(OutOfStockBlock, { isComingSoon: isComingSoon, showSimilarProductsOOS: showSimilarProductsOOS, openSimilarModal: props.openSimilarModal })) : (React.createElement("div", { className: "button-modal-container" },
        React.createElement("button", { className: classNames('button button--secondary size-selection-button', { disabled: props.currentSize && isSizeDisabled(props.currentSize) }), onClick: () => {
                toggleModal(!openModal);
            }, onKeyDown: (e) => {
                if ((openModal && e.key == 'Tab') || e.key == 'ArrowDown') {
                    e.preventDefault();
                    toggleModal(false);
                }
            }, role: isMobile ? 'button' : 'combobox', "aria-haspopup": "listbox", "aria-expanded": isMobile ? (openModal ? 'true' : 'false') : undefined, id: "size-combobox" },
            buttonText,
            openModal ? React.createElement("i", { className: "chevron icon--chevron-up" }) : React.createElement("i", { className: "chevron icon--chevron-down" })),
        isMobile ? (React.createElement(ModalDialog, { role: "dialog", isOpen: openModal, onRequestClose: () => {
                toggleModal(false);
            }, className: "size-selection", overlayClassName: "size-selection", contentLabel: "Select a Size", buttonInHeader: true, subjsx: props.showSizeFit && fittingCaption },
            React.createElement("div", { style: { '--size-label-length': longestSizeChars * 8 + 'px', '--scrollbar-height': `${scrollBarHeight}px`, '--scroll-top': `${scrollTop}px` } }, SizesBlock))) : (openModal && (React.createElement(React.Fragment, null, createPortal(React.createElement(FocusTrap, { focusTrapOptions: { allowOutsideClick: true }, active: !notifyOpen },
            React.createElement("div", { id: "size-selection-combobox", className: "size-selection size-selection-combobox", style: { bottom: `${bottom}px`, '--size-label-length': longestSizeChars * 8 + 'px' } },
                props.showSizeFit && fittingCaption(),
                SizesBlock)), document.getElementById('size-selector-combobox-container')))))));
};
export default SizeSelectorButton;
